import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { catchError, first, firstValueFrom, map, Observable, of, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { DataService } from './data.service';
import MkgCashTitleLog from '../class/mkg-cash-title-log';
import { CreateCashTitleMovimentationParamethers } from '../interface/cash-log-interfaces';
import { PaginatedTitle } from './cash-title.service';
import { ClientService } from './client.service';
import { SupplierService } from './supplier.service';
import { Supplier } from '../interface/supplier';
import { Client } from '../interface/client';
import { CashTitle } from '../interface/cash-title';
import CashTitleMovementation from '../interface/cash-title-movementation';
import { ApiPaginatedResponse, ApiTitleLogsPaginatedQueryParams, PaginationService } from './pagination.service';
import { API_ERRORS } from '../shared/lists/errors';

@Injectable({
  providedIn: 'root'
})
export class CashTitleLogService {

  private _url: string = `${environment.mkgoURL}/api/v1/log-finan`;
  
  constructor(
    private _dataService: DataService,
    private _http: HttpClient,
    private _clientService: ClientService,
    private _supplierService: SupplierService,
  ) { }

  async create({ title, origin, creationCompany, isSupplier, movementType, targetCompany, userId, invoiceId, valueChanged, renegociationTitlesId, invoiceNumber, orderId, osId, titleErro, triggerFromAdmCardCreation, advancesUsed, advanceTargetId, titleBeforeChanges }: CreateCashTitleMovimentationParamethers) {
    const cashTitleMovementation = new MkgCashTitleLog({
      title,
      creationCompany,
      isSupplier,
      movementType,
      origin,
      targetCompany,
      userId,
      invoiceId,
      valueChanged,
      invoiceNumber,
      orderId,
      osId,
      renegociationTitlesId,
      titleErro,
      triggerFromAdmCardCreation,
      advancesUsed,
      advanceTargetId,
      titleBeforeChanges
    }, 1);

    const url = this._url;
    const options = await firstValueFrom(this._dataService.httpOptions(true));

    return await firstValueFrom(this._http.post(url, cashTitleMovementation, options)); 
  }

  async getAll() {
    const options = await firstValueFrom(this._dataService.httpOptions(true));
    return await firstValueFrom(this._http.get<{ logFinans: CashTitleMovementation[] }>(this._url, options)); 
  }

  getPaginated(apiParams: ApiTitleLogsPaginatedQueryParams): Observable<ApiPaginatedResponse<CashTitleMovementation>> {
    const url = `${this._url}/pages`;
    let params = PaginationService.getParams(apiParams);
    
    return this._dataService.httpOptions(true, params).pipe(
      switchMap(options => this._http.get<ApiPaginatedResponse<CashTitleMovementation>>(url, options)),
      first(),
      catchError(err => {
        /** If no one data is found, the API will return a error */
        if (err.error.error === API_ERRORS.notFound) {
          return of(PaginationService.notFoundErrorDataToReturn());
        } else {
          throw err;
        }
      }),
      switchMap(resp => {
        // the API return 100 docs by defult (even if all=1), so if we want all docs we must call it again with param `limit`
        if (apiParams.all && resp.hasNextPage) {
          apiParams.limit = resp.totalDocs;
          params = PaginationService.getParams(apiParams);
          return this._dataService.httpOptions(true, params).pipe(
            switchMap(options2 => this._http.get<ApiPaginatedResponse<CashTitleMovementation>>(url, options2)),
            first()
          )
        } else {
          return of(resp)
        }
      }),
      map(resp => resp)
    );
  }

  async getIsSupplier(clientOrSupplier: CashTitle | PaginatedTitle, extraDataArrays?: {
    suppliersArray: Supplier[];
    clientsArray: Client[];
  }) {
    if ((clientOrSupplier as PaginatedTitle)?.["supplier_client"]?.["kind"]) {
      return (clientOrSupplier as PaginatedTitle)?.["supplier_client"]?.["kind"] === "Supplier";
    }
    
    let isSupplier: boolean;
    let client_supplier: Supplier | Client;
    const clientOrSupplierId = clientOrSupplier?.["supplier_clientId"] ?? clientOrSupplier?.["supplier_client"]?.["_id"] ?? clientOrSupplier?.["supplier_client"]?.["id"];

    if (extraDataArrays && (extraDataArrays?.clientsArray?.length && extraDataArrays?.suppliersArray?.length)) {
      const { clientsArray, suppliersArray } = extraDataArrays;

      try {
        client_supplier = clientsArray.find(clientToFind => clientToFind === clientOrSupplierId)
        if (client_supplier?.id) {
          isSupplier = false;
        }
      } catch (error) {
        console.error(error);
      }

      if (client_supplier !== undefined && client_supplier !== null) {
        try {
          client_supplier = suppliersArray.find(supplierToFind => supplierToFind === clientOrSupplierId)
          if (client_supplier?.id) {
            isSupplier = true;
          }
        } catch (error) {
          console.error(error);
        }
      }

      if (isSupplier !== undefined && isSupplier !== null) {
        return isSupplier;
      }
    }

    try {
      client_supplier = await this._clientService.get(clientOrSupplierId);
      isSupplier = !!client_supplier?.id;
    } catch (error) {
      console.error(error);
    }

    if (!client_supplier) {
      try {
        client_supplier = await this._supplierService.get(clientOrSupplierId);
        isSupplier = !!client_supplier?.id;

      } catch (error) {
        console.error(error);
      }
    }

    return isSupplier;
  }
}
